import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { getEcho } from 'services/realtime/echo';
import { privateAPI } from 'services/private';
import { useMarkDocuSyncAsReadMutation } from 'services/private/docu-sync';
import { useMarkRequestPlusAsReadMutation } from 'services/private/request-plus';
import { useMarkTaskVaultAsReadMutation } from 'services/private/task-vault';
import { useMarkPasswordRequestsAsReadMutation } from 'services/private/password-request';
import {
  syncDocuSyncSidebarCount,
  syncContactsSidebarCount,
  syncDocumentsSidebarCount,
  syncTaskVaultSidebarCount,
  syncRequestPlusSidebarCount,
  syncPasswordRequestsSidebarCount,
  syncTransactionsSidebarCount,
  updateContactsNotifications,
  updateDocuSyncNotifications,
  updateDocumentsNotifications,
  updateRequestPlusNotifications,
  updateTaskVaultNotifications,
  updateTransactionsNotifications,
  updateTransactionalQuestionsNotifications,
  updatePasswordRequestsNotifications,
} from 'store/slices/authSlice';
import upsertPasswordRequestListItem from 'utilities/passwordRequestCache';
import upsertClientContactListItem from 'utilities/clientContactCache';
import upsertClientRequestListItem from 'utilities/clientRequestCache';
import upsertTransactionListItem from 'utilities/transactionCache';
import {
  isContactsListRoute,
  isContactsRoute,
  isDocuSyncFolderRoute,
  isDocuSyncRoute,
  isDocumentsRoute,
  isPendingAccessRequestsListRoute,
  isPendingAccessRequestsRoute,
  isRequestPlusDetailRoute,
  isRequestPlusListRoute,
  isRequestPlusRoute,
  getClientRequestListQueryArgs,
  isTaskVaultProjectRoute,
  isTaskVaultRoute,
  isTransactionsRoute,
  isTransactionsListRoute,
  getTransactionListQueryArgs,
} from 'utilities/helpers';

const mergeUnreadDocumentItems = (currentItems, payload) => {
  if (payload?.unread_document_items) {
    return payload.unread_document_items;
  }

  if (payload?.item_id && payload?.item_type) {
    const nextItem = { id: Number(payload.item_id), type: payload.item_type };
    const exists = currentItems.some(
      item => Number(item.id) === nextItem.id && item.type === nextItem.type
    );

    return exists ? currentItems : [...currentItems, nextItem];
  }

  return currentItems;
};

const applyDocuSyncReadResponse = (dispatch, response) => {
  dispatch(
    updateDocuSyncNotifications({
      count: response?.data?.unread_folders_count ?? 0,
      folderIds: response?.data?.unread_folder_ids ?? [],
    })
  );
};

const applyTaskVaultReadResponse = (dispatch, response) => {
  dispatch(
    updateTaskVaultNotifications({
      count: response?.data?.unread_tasks_count ?? 0,
      projectIds: response?.data?.unread_project_ids ?? [],
    })
  );
};

const applyRequestPlusReadResponse = (dispatch, response) => {
  dispatch(
    updateRequestPlusNotifications({
      count: response?.data?.unread_requests_count ?? 0,
      projectIds: response?.data?.unread_request_project_ids ?? [],
    })
  );
};

const mergeUnreadIds = (currentIds, payloadId) => {
  if (!payloadId) {
    return currentIds;
  }

  return [...new Set([...currentIds.map(Number), Number(payloadId)])];
};

const useClientPortalNotifications = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const locationRef = useRef(location.pathname);
  const locationSearchRef = useRef(location.search);
  const previousPathRef = useRef(location.pathname);
  const [markTaskVaultAsRead] = useMarkTaskVaultAsReadMutation();
  const [markRequestPlusAsRead] = useMarkRequestPlusAsReadMutation();
  const [markDocuSyncAsRead] = useMarkDocuSyncAsReadMutation();
  const [markPasswordRequestsAsRead] = useMarkPasswordRequestsAsReadMutation();
  const {
    isAuthenticated,
    user,
    unreadContactIds,
    unreadDocuSyncFolderIds,
    unreadDocumentItems,
    unreadTaskVaultProjectIds,
    unreadRequestProjectIds,
    unreadPasswordRequestIds,
    unreadTransactionIds,
    unreadTransactionalQuestionIds,
    unreadTransactionalQuestionsCount,
  } = useSelector(state => state.auth);
  const unreadContactIdsRef = useRef(unreadContactIds);
  const unreadDocuSyncFolderIdsRef = useRef(unreadDocuSyncFolderIds);
  const unreadDocumentItemsRef = useRef(unreadDocumentItems);
  const unreadTaskVaultProjectIdsRef = useRef(unreadTaskVaultProjectIds);
  const unreadRequestProjectIdsRef = useRef(unreadRequestProjectIds);
  const unreadPasswordRequestIdsRef = useRef(unreadPasswordRequestIds);
  const unreadTransactionIdsRef = useRef(unreadTransactionIds);
  const unreadTransactionalQuestionIdsRef = useRef(unreadTransactionalQuestionIds);
  const unreadTransactionalQuestionsCountRef = useRef(unreadTransactionalQuestionsCount);

  unreadContactIdsRef.current = unreadContactIds;
  unreadDocuSyncFolderIdsRef.current = unreadDocuSyncFolderIds;
  unreadDocumentItemsRef.current = unreadDocumentItems;
  unreadTaskVaultProjectIdsRef.current = unreadTaskVaultProjectIds;
  unreadRequestProjectIdsRef.current = unreadRequestProjectIds;
  unreadPasswordRequestIdsRef.current = unreadPasswordRequestIds;
  unreadTransactionIdsRef.current = unreadTransactionIds;
  unreadTransactionalQuestionIdsRef.current = unreadTransactionalQuestionIds;
  unreadTransactionalQuestionsCountRef.current = unreadTransactionalQuestionsCount;

  locationRef.current = location.pathname;
  locationSearchRef.current = location.search;

  useEffect(() => {
    const previousPath = previousPathRef.current;
    const currentPath = location.pathname;

    if (isDocuSyncRoute(previousPath) && !isDocuSyncRoute(currentPath)) {
      dispatch(syncDocuSyncSidebarCount());
    }

    if (isContactsRoute(previousPath) && !isContactsRoute(currentPath)) {
      dispatch(syncContactsSidebarCount());
    }

    if (isDocumentsRoute(previousPath) && !isDocumentsRoute(currentPath)) {
      dispatch(syncDocumentsSidebarCount());
    }

    if (isTaskVaultRoute(previousPath) && !isTaskVaultRoute(currentPath)) {
      dispatch(syncTaskVaultSidebarCount());
    }

    if (isRequestPlusRoute(previousPath) && !isRequestPlusRoute(currentPath)) {
      dispatch(syncRequestPlusSidebarCount());
    }

    if (isPendingAccessRequestsRoute(previousPath) && !isPendingAccessRequestsRoute(currentPath)) {
      dispatch(syncPasswordRequestsSidebarCount());
    }

    if (isTransactionsRoute(previousPath) && !isTransactionsRoute(currentPath)) {
      dispatch(syncTransactionsSidebarCount());
    }

    previousPathRef.current = currentPath;
  }, [dispatch, location.pathname]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      return undefined;
    }

    const echo = getEcho();

    if (!echo) {
      return undefined;
    }

    const channelName = `App.Models.User.User.${user.id}`;
    const channel = echo.private(channelName);

    channel.listen('.client-task.assigned', payload => {
      const pathname = locationRef.current;
      const projectId = payload?.project_id;

      dispatch(
        privateAPI.util.invalidateTags([
          { type: 'TaskList', id: String(projectId) },
          'ProjectsList',
        ])
      );

      if (isTaskVaultProjectRoute(pathname, projectId)) {
        markTaskVaultAsRead({ projectId })
          .unwrap()
          .then(response => {
            applyTaskVaultReadResponse(dispatch, response);
          })
          .catch(() => {});

        return;
      }

      const projectIds = payload?.unread_project_ids ?? mergeUnreadIds(unreadTaskVaultProjectIdsRef.current, projectId);

      dispatch(
        updateTaskVaultNotifications({
          count: payload?.unread_tasks_count ?? projectIds.length,
          projectIds,
        })
      );
    });

    channel.listen('.client-request-plus.created', payload => {
      const pathname = locationRef.current;
      const projectId = payload?.project_id;
      const listQueryArgs = getClientRequestListQueryArgs(locationSearchRef.current);
      const onListPage = isRequestPlusListRoute(pathname);

      if (!isRequestPlusDetailRoute(pathname, projectId)) {
        if (onListPage && payload?.project?.id) {
          upsertClientRequestListItem(dispatch, payload.project, listQueryArgs);
        }

        dispatch(privateAPI.util.invalidateTags(['ClientRequestList']));
      }

      if (isRequestPlusDetailRoute(pathname, projectId)) {
        markRequestPlusAsRead({ projectId })
          .unwrap()
          .then(response => {
            applyRequestPlusReadResponse(dispatch, response);
          })
          .catch(() => {});

        dispatch(privateAPI.util.invalidateTags(['ClientRequestListDetail']));

        return;
      }

      const projectIds = payload?.unread_request_project_ids
        ?? mergeUnreadIds(unreadRequestProjectIdsRef.current, projectId);

      dispatch(
        updateRequestPlusNotifications({
          count: payload?.unread_requests_count ?? projectIds.length,
          projectIds,
        })
      );
    });

    channel.listen('.client-docu-sync.updated', payload => {
      const pathname = locationRef.current;
      const folderId = payload?.folder_id;

      if (isDocuSyncFolderRoute(pathname, folderId)) {
        markDocuSyncAsRead({ folderId })
          .unwrap()
          .then(response => {
            applyDocuSyncReadResponse(dispatch, response);
          })
          .catch(() => {});

        return;
      }

      const folderIds = payload?.unread_folder_ids ?? mergeUnreadIds(unreadDocuSyncFolderIdsRef.current, payload?.folder_id);

      dispatch(
        updateDocuSyncNotifications({
          count: payload?.unread_folders_count ?? folderIds.length,
          folderIds,
        })
      );

      dispatch(privateAPI.util.invalidateTags(['ListDocuFolders']));
    });

    channel.listen('.client-contact.added', payload => {
      const pathname = locationRef.current;
      const onContactsList = isContactsListRoute(pathname);

      if (onContactsList && payload?.contact?.id) {
        upsertClientContactListItem(dispatch, payload.contact);
      }

      dispatch(privateAPI.util.invalidateTags(['ListContacts']));

      const contactIds = payload?.unread_contact_ids ?? mergeUnreadIds(unreadContactIdsRef.current, payload?.contact_id);

      dispatch(
        updateContactsNotifications({
          count: payload?.unread_contacts_count ?? contactIds.length,
          contactIds,
        })
      );
    });

    channel.listen('.client-transaction.shared', payload => {
      const pathname = locationRef.current;
      const listQueryArgs = getTransactionListQueryArgs(locationSearchRef.current);
      const onListPage = isTransactionsListRoute(pathname);

      if (onListPage && payload?.transaction?.id) {
        upsertTransactionListItem(dispatch, payload.transaction, listQueryArgs);
      }

      dispatch(privateAPI.util.invalidateTags(['TransactionList']));

      const transactionIds = payload?.unread_transaction_ids
        ?? mergeUnreadIds(unreadTransactionIdsRef.current, payload?.transaction_id);

      dispatch(
        updateTransactionsNotifications({
          count: payload?.transactions_count ?? transactionIds.length,
          transactionIds,
        })
      );
    });

    channel.listen('.client-transactional-question.created', payload => {
      const questionIds = payload?.unread_transactional_question_ids
        ?? mergeUnreadIds(unreadTransactionalQuestionIdsRef.current, payload?.question_id);

      dispatch(
        updateTransactionalQuestionsNotifications({
          count: payload?.unread_transactional_questions_count ?? unreadTransactionalQuestionsCountRef.current,
          questionIds,
        })
      );

      dispatch(privateAPI.util.invalidateTags(['TransactionalQuestionList']));
    });

    channel.listen('.client-password-request.created', payload => {
      const pathname = locationRef.current;
      const searchParams = new URLSearchParams(locationSearchRef.current);
      const page = Number(searchParams.get('page')) || 1;
      const onListPage = isPendingAccessRequestsListRoute(pathname);

      if (onListPage && payload?.password_request?.id) {
        upsertPasswordRequestListItem(dispatch, payload.password_request, page);
      }

      dispatch(privateAPI.util.invalidateTags(['passwordRequestsList']));

      const requestIds = payload?.unread_password_request_ids
        ?? mergeUnreadIds(unreadPasswordRequestIdsRef.current, payload?.password_request_id);

      dispatch(
        updatePasswordRequestsNotifications({
          count: payload?.unread_password_requests_count ?? requestIds.length,
          requestIds,
        })
      );
    });

    channel.listen('.client-documents.updated', payload => {
      const items = mergeUnreadDocumentItems(unreadDocumentItemsRef.current, payload);

      dispatch(
        updateDocumentsNotifications({
          count: payload?.unread_documents_count ?? items.length,
          items,
        })
      );

      dispatch(privateAPI.util.invalidateTags(['ListDocuments']));
    });

    return () => {
      channel.stopListening('.client-task.assigned');
      channel.stopListening('.client-request-plus.created');
      channel.stopListening('.client-docu-sync.updated');
      channel.stopListening('.client-contact.added');
      channel.stopListening('.client-transaction.shared');
      channel.stopListening('.client-transactional-question.created');
      channel.stopListening('.client-password-request.created');
      channel.stopListening('.client-documents.updated');
      echo.leave(channelName);
    };
  }, [
    dispatch,
    isAuthenticated,
    markDocuSyncAsRead,
    markPasswordRequestsAsRead,
    markRequestPlusAsRead,
    markTaskVaultAsRead,
    user?.id,
  ]);
};

export default useClientPortalNotifications;
